import * as React from "react"
import * as TooltipPrimitive from "@radix-ui/react-tooltip"

import { cn } from "@/lib/utils"

const TooltipProvider = TooltipPrimitive.Provider

const Tooltip = TooltipPrimitive.Root

const TooltipTrigger = TooltipPrimitive.Trigger

const TooltipContent = React.forwardRef<
  React.ElementRef<typeof TooltipPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof TooltipPrimitive.Content>
>(({ className, sideOffset = 4, collisionPadding = 16, style, ...props }, ref) => {
  const [sidebarWidth, setSidebarWidth] = React.useState(
    () => typeof window !== 'undefined' ? getComputedStyle(document.documentElement).getPropertyValue('--sidebar-width').trim() : '16rem'
  );
  const [boundary, setBoundary] = React.useState<HTMLElement | null>(null);

  React.useEffect(() => {
    // When the tooltip opens, check if it's inside our specific dialog
    if (document.documentElement.classList.contains('client-form-popup-open')) {
      const dialogElement = document.querySelector<HTMLElement>('[role="dialog"]');
      setBoundary(dialogElement);
    }

    const handleSidebarToggle = () => {
      setSidebarWidth(getComputedStyle(document.documentElement).getPropertyValue('--sidebar-width').trim());
    };

    window.addEventListener('sidebar-toggled', handleSidebarToggle);

    return () => {
      window.removeEventListener('sidebar-toggled', handleSidebarToggle);
    }
  }, []);

  const dynamicCollisionPadding = React.useMemo(() => {
    const basePadding = typeof collisionPadding === 'number'
      ? { top: collisionPadding, right: collisionPadding, bottom: collisionPadding, left: collisionPadding }
      : { top: 16, right: 16, bottom: 16, left: 16, ...collisionPadding };

    // If inside a dialog, or window is not available, don't add extra viewport-based padding.
    if (boundary || typeof window === 'undefined') return basePadding;

    // Check if we're on mobile using CSS media query
    const isMobile = window.matchMedia("(max-width: 639px)").matches;
    
    if (isMobile) { // On mobile, account for the bottom nav bar
      const navHeight = 64; // h-16
      basePadding.bottom = Math.max(basePadding.bottom, navHeight + 16);
    } else { // On desktop, account for the current sidebar width
      const remValue = parseFloat(sidebarWidth);
      const rootFontSize = typeof window !== 'undefined' ? parseFloat(getComputedStyle(document.documentElement).fontSize) : 16;
      if (!isNaN(remValue) && !isNaN(rootFontSize)) {
        basePadding.left = Math.max(basePadding.left, (remValue * rootFontSize) + 16);
      }
    }
    return basePadding;
  }, [collisionPadding, sidebarWidth, boundary]);


  return (
    <TooltipPrimitive.Portal key={sidebarWidth}>
      <TooltipPrimitive.Content
        ref={ref}
        sideOffset={sideOffset}
        sticky="always"
        collisionBoundary={boundary ?? undefined}
        collisionPadding={dynamicCollisionPadding}
        style={style}
        onWheel={(e) => {
          // When a dialog is open, it often locks body scroll. Tooltips are portalled
          // to the body, so their wheel events bubble up and get blocked.
          // Stopping propagation here allows the tooltip content to be scrolled.
          e.stopPropagation()
        }}
        className={cn(
          "z-[300] max-w-[var(--radix-tooltip-content-available-width)] max-h-[var(--radix-tooltip-content-available-height)] overflow-y-auto scrollbar-visible rounded-lg border-2 border-border/50 bg-gradient-to-br from-popover to-popover/95 backdrop-blur-sm px-5 py-4 text-xs text-popover-foreground shadow-xl shadow-black/10 animate-in fade-in-0 zoom-in-95 data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2",
          className
        )}
        {...props}
      />
    </TooltipPrimitive.Portal>
  )
})
TooltipContent.displayName = TooltipPrimitive.Content.displayName

export { Tooltip, TooltipTrigger, TooltipContent, TooltipProvider }
